import { useEffect, useState } from "react";
import { useConnectivity, useOnlineStatus } from "../../hooks/useOnlineStatus";

/**
 * Connectivity pill — React port of
 * label_studio/users/templates/users/new-ui/_net_indicator.html.
 *
 * Visual contract: an icon-only pill that expands to show a status
 * label on hover (mouse) or tap (touch). Green when online, amber
 * when offline.
 *
 * Online state comes from ``useOnlineStatus`` — same hook the
 * menubar's ``OfflinePill`` uses. It polls ``/api/version/`` so the
 * pill flips to "offline" when the *cloud* is unreachable, not just
 * when ``navigator.onLine`` says the radio is dropped. Important on
 * Capacitor where the WebView always reports onLine=true even when
 * the cloud Django at ``localhost:8080`` is stopped (panel.py
 * ``stop_cloud``); the user otherwise stares at a green "Connected"
 * pill while their login form 502s.
 *
 * Mounted inside the form-header next to the page heading. On the web
 * (Django-served auth pages) the equivalent button is hidden via the
 * `body.is-web` class; in the React bundle it stays visible because
 * mobile/desktop genuinely benefit from at-a-glance connectivity
 * feedback.
 */
export const NetIndicator = () => {
  const online = useOnlineStatus();
  // Same four-state model as the in-app menubar pill so the login
  // page and the app never disagree about WHY things aren't working:
  //   no-internet  → red   "No internet"
  //   server-down  → red   "Server unreachable"
  //   slow         → amber "Slow connection"
  //   online       → green icon-only "Connected"
  const connectivity = useConnectivity();
  const [revealed, setRevealed] = useState(false);
  const degraded = !online || connectivity === "slow";

  // Auto-reveal label briefly when state flips. 3.5s matches the
  // tap-reveal timer in the Django template's inline JS. Degraded
  // states stay expanded (see ``is-degraded`` in Auth.css) — a
  // one-word red pill is the whole point when you can't sign in.
  useEffect(() => {
    setRevealed(true);
    const id = window.setTimeout(() => setRevealed(false), 3500);
    return () => window.clearTimeout(id);
  }, [online, connectivity]);

  const kind = !online
    ? connectivity === "no-internet"
      ? "no-internet"
      : "server-down"
    : connectivity === "slow"
      ? "slow"
      : "online";

  const cls = [
    "afri-net-indicator",
    online ? "is-online" : "is-offline",
    degraded ? "is-degraded" : "",
    `is-${kind}`,
    revealed ? "is-revealed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const label =
    kind === "no-internet"
      ? "Offline"
      : kind === "server-down"
        ? "Server unreachable"
        : kind === "slow"
          ? "Slow connection"
          : "Connected";

  return (
    <button
      type="button"
      aria-live="polite"
      aria-label={label}
      title={label}
      className={cls}
      onClick={() => {
        setRevealed(true);
        window.setTimeout(() => setRevealed(false), 3000);
      }}
    >
      <svg
        className="afri-net-icon afri-net-icon_on"
        aria-hidden="true"
        viewBox="0 0 24 24"
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M5 12.55a11 11 0 0 1 14.08 0" />
        <path d="M1.42 9a16 16 0 0 1 21.16 0" />
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
        <line x1="12" y1="20" x2="12.01" y2="20" />
      </svg>
      <svg
        className="afri-net-icon afri-net-icon_off"
        aria-hidden="true"
        viewBox="0 0 24 24"
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="1" y1="1" x2="23" y2="23" />
        <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
        <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
        <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
        <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
        <line x1="12" y1="20" x2="12.01" y2="20" />
      </svg>
      <span className="afri-net-text">{label}</span>
    </button>
  );
};
